import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';

export interface PageHeaderProps extends Omit<ComponentProps<'header'>, 'title'> {
  /** Rendered above the title — usually `Breadcrumbs`. */
  above?: ReactNode;
  title: ReactNode;
  /** A quiet line under the title: counts, dates, ownership. */
  meta?: ReactNode;
  /** Buttons, pushed to the trailing edge and vertically centred on the title. */
  actions?: ReactNode;
}

/**
 * The band at the top of every screen: where you are, what it is called, and
 * what you can do to it.
 *
 * Exists because the arrangement is load-bearing and easy to get subtly wrong —
 * the actions sit on the title's line, not below it, and the title is the
 * display face at its largest.
 */
export function PageHeader({ above, title, meta, actions, className, ...props }: PageHeaderProps) {
  return (
    <header className={cn('flex flex-col gap-3', className)} {...props}>
      {above}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink">{title}</h1>
          {meta ? <p className="text-bodysm text-ink-muted">{meta}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-3">{actions}</div> : null}
      </div>
    </header>
  );
}
